import React from 'react';
import { useTranslation } from 'react-i18next';
import TagsService from '../../services/use_case/tags/TagsService';
import Tag, { getOption } from '../../models/Tag';

import { withAsyncPaginate } from "react-select-async-paginate";
import Creatable from 'react-select/creatable';

const AsyncPaginateCreatable = withAsyncPaginate(Creatable as any) as React.FC<any>;


const selectStyles = {
    control: (styles: any, state: any) => ({
        ...styles,
        backgroundColor: 'rgba(35, 34, 35, 0.65)',
        border: state.isFocused
            ? '1px solid transparent'
            : '1px solid rgba(255, 255, 255, 0.07)',
        borderRadius: '6px',
        boxShadow: state.isFocused
            ? '0 0 0 1.5px #0F96C2, 0 0 16px rgba(15, 150, 194, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.03)'
            : 'none',
        fontSize: '.8rem',
        minHeight: '38px',
        transition: 'border-color 0.25s ease, box-shadow 0.25s ease, background 0.25s ease',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        "&:hover": {
            borderColor: state.isFocused ? 'transparent' : 'rgba(15, 150, 194, 0.3)',
            backgroundColor: 'rgba(38, 37, 38, 0.8)',
        },
    }),
    menu: (provided: any) => ({
        ...provided,
        backgroundColor: 'rgba(26, 25, 26, 0.97)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '6px',
        color: '#969696',
        fontSize: '.8rem',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.45)',
        overflow: 'hidden',
    }),
    option: (styles: any, meta: any) => {
        const { data, isDisabled/*, isFocused, isSelected*/ } = meta;
        const color = data.color;
        return {
            ...styles,
            backgroundColor: 'transparent',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            color: color || '#969696',
            transition: 'background 0.15s ease, color 0.15s ease',
            "&:hover": {
                backgroundColor: 'rgba(15, 150, 194, 0.12)',
                color: '#d0d0d0',
            },
            "&:active": {
                backgroundColor: 'rgba(15, 150, 194, 0.2)',
                color: '#d0d0d0',
            },
        };
    },
    singleValue: (provided: any, state: any) => ({
        ...provided,
        opacity: state.isDisabled ? 0.45 : 1,
        transition: 'opacity 250ms',
        color: '#969696',
    }),
    indicatorSeparator: (provided: any) => ({
        ...provided,
        backgroundColor: 'rgba(255, 255, 255, 0.07)',
    }),
    dropdownIndicator: (provided: any, state: any) => ({
        ...provided,
        color: state.isFocused ? '#0F96C2' : '#656465',
        transition: 'color 0.25s ease',
        '&:hover': { color: '#0F96C2' },
    }),
    clearIndicator: (provided: any) => ({
        ...provided,
        color: '#656465',
        '&:hover': { color: '#c82424' },
    }),
    input: (defaultStyles: any) => ({
        ...defaultStyles,
        color: '#969696',
    }),
    multiValue: (styles: any) => ({
        ...styles,
        backgroundColor: 'rgba(15, 150, 194, 0.15)',
        borderRadius: '4px',
    }),
    multiValueLabel: (styles: any) => ({
        ...styles,
        backgroundColor: 'transparent',
        color: '#0F96C2',
        fontWeight: '500',
        fontSize: '0.75rem',
    }),
    multiValueRemove: (styles: any) => ({
        ...styles,
        color: '#0F96C2',
        borderRadius: '0 4px 4px 0',
        '&:hover': {
            backgroundColor: 'rgba(200, 36, 36, 0.2)',
            color: '#c82424',
            cursor: 'pointer',
        },
    }),
};

interface CreatableTagsSelectProps {
    value: any;
    onChange: any;
    cache?: any;
    closeMenuOnSelect?: boolean;
    onCreateOption?: (inputValue: string) => Promise<void>;
    isCreating?: boolean;
};

const CreatableTagsSelect: React.FC<CreatableTagsSelectProps> = (props: CreatableTagsSelectProps) => {

    const [t] = useTranslation();

    let tagsService: TagsService

    const _fetchTags = async (search:string, prevOptions: any = null , { page }: { page: number }) => {
        try {

            tagsService = new TagsService();
            let response: any = await tagsService.fetch(page, search);
            const responseData = response.data?.data;

            let tags: Tag[] = response.data?.data?.data ?? [];

            let data = tags.map((tag: Tag) => getOption(tag) );

            return {
                options: data,
                hasMore: responseData?.last_page > page,
                additional: {
                    page: responseData.current_page + 1
                },
            }
        }
        catch(error) {
            return {
                options: [],
                hasMore: false,
                additional: {
                    page: 1
                }
            };
        }
    }

    return (
        <>
            <AsyncPaginateCreatable
                value={props.value}
                onChange={props.onChange}
                closeMenuOnSelect={props.closeMenuOnSelect}
                loadOptions={_fetchTags}
                styles={selectStyles}
                isSearchable={true}
                isMulti={true}
                noOptionsMessage={() => t('No Result')}
                loadingMessage={() => t('Loading...')}
                placeholder={t('Select_a_tag')}
                debounceTimeout={500}
                additional={{ page: 1 }}
                cacheUniqs={[props.cache]}
                onCreateOption={props.onCreateOption}
                isLoading={props.isCreating}
                formatCreateLabel={(inputValue: string) => `${t('Create_tag')}: "${inputValue}"`}
            />
        </>
    );

};

export default React.memo(CreatableTagsSelect);
